import { ReactElement, useCallback, useMemo } from "react";

import { FieldValues, useController, useWatch } from "react-hook-form";

import { useTranslate } from "../../../hooks/useTranslate";
import { useValidators } from "../../../hooks/useValidators";

import { ControlledDateTimePicker } from "./ControlledDateTimePicker";
import { setDateFromUtc, setDateToUtc } from "./timezone";
import { DateTimePickerProps } from "./types";

import { getLabel, isDate, isInputRequired } from "@/helpers/validators";

// This wrapper will use the timezone to handle the conversion of the date in the picker and the form value

export function DashboardDateTimePicker<T extends FieldValues>(
  props: DateTimePickerProps<T>
): ReactElement<DateTimePickerProps<T>> {
  const { control, name, rules, defaultValue, onChange, timezone } = props;
  const { t } = useTranslate();
  const formDate = useWatch({ control, name }) as Date;

  const initialDate = formDate || defaultValue || new Date();
  const validators = useValidators([...rules, isDate(t("dashboard", "datePickerErrorMessage.invalidDate"))]);
  const isRequired = isInputRequired(validators);

  const { field } = useController<T>({
    name,
    control,
    defaultValue: initialDate as never,
    rules: validators,
  });

  const handleChange: NonNullable<DateTimePickerProps<T>["onChange"]> = useCallback(
    (newDate) => {
      const date = newDate ? setDateToUtc(newDate, timezone) : null;

      onChange?.(date);
      field.onChange(date);
    },
    [field, onChange, timezone]
  );

  const formattedValue = useMemo(
    () => field.value && setDateFromUtc(new Date(field.value as Date), timezone),
    [field.value, timezone]
  );

  return (
    <ControlledDateTimePicker
      {...props}
      {...field}
      inputRef={field.ref}
      disabled={props.disabled}
      value={formattedValue ? new Date(formattedValue) : null}
      onChange={handleChange}
      label={getLabel(isRequired, props.label, props.noAsterisk)}
    />
  );
}
